<script setup lang="ts">
import type { AudienceRule } from '@/types/types'
import { useVModel } from '@vueuse/core'
const { t } = useI18n()
const props = withDefaults(
  defineProps<{
    name: string
    rule: AudienceRule
    operators?: string[]
    disabled?: boolean
    suffix?: string
    min?: number
    max?: number
    errors?: boolean
  }>(),
  {
    operators: () => ['eq', 'gt', 'lt', 'between', 'lte', 'gte'],
    disabled: false,
    suffix: null,
    min: 0,
    max: null
  }
)

const rule = useVModel(props, 'rule')
const emit = defineEmits(['hasError'])

const selectOperator = (operator: string) => {
  if (!rule.value) {
    return
  }

  rule.value.operator = operator

  if (operator !== 'between') {
    rule.value.value = [rule.value.value[0]]
  }
}

const validateRuleValues = () => {
  if (!Array.isArray(rule.value.value)) {
    rule.value.value = []
  }

  if (rule.value.operator === 'between' && rule.value.value.length < 2) {
    rule.value.value = [null, null]
  }
}

validateRuleValues()

const hasError = () => {
  if (rule.value.operator === 'between') {
    const min = rule.value.value[0]
    const max = rule.value.value[1]

    if (!isDefined(min) && !isDefined(max)) {
      emit('hasError', false)
      return false
    }

    if (min > max || min === max || !isDefined(max) || !isDefined(min)) {
      emit('hasError', true)
      return true
    }
    emit('hasError', false)
    return false
  }

  return false
}

const capitalize = (str: string): string => {
  if (!str) {
    return ''
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const operatorOptions = computed(() => {
  if (!props.operators) {
    return []
  }

  return props.operators.map((op) => ({
    id: op,
    text: capitalize(t(`pages.audience.operators.${op}`))
  }))
})

const valueACorrectlySet = computed(() => {
  if (rule?.value?.operator === 'between') {
    return isDefined(rule?.value?.value?.[0]) && isDefined(rule?.value?.value?.[1])
  }

  return true
})

const showErrorMessage = computed(() => {
  return (hasError() || props.errors) && valueACorrectlySet.value
})

const isDefined = (value: number | string | null | undefined) => {
  return value !== null && value !== undefined && value !== ''
}
</script>

<template>
  <div v-if="rule" v-bind="$attrs" class="flex flex-col sm:grid sm:grid-cols-2 sm:gap-4">
    <div class="sm:col-span-1">
      <ComboboxSelect
        value-key="id"
        label-key="text"
        trigger-class="w-full"
        :model-value="rule.operator ?? 'eq'"
        :disabled="disabled"
        :placeholder="t('pages.audience.operators.placeholder', { ruleName: name })"
        :options="operatorOptions"
        @update:model-value="selectOperator"
      />
    </div>
    <div class="flex w-full flex-row items-center gap-4 sm:col-span-1">
      <AppFormInput
        :id="`${name}-first-value`"
        v-model="rule.value[0]"
        type="number"
        class="h-full sm:flex 2xl:justify-end"
        :class="rule?.operator === 'between' ? 'w-auto sm:w-full' : 'sm:w-full'"
        :display-arrows="false"
        :disabled="disabled"
        :aria-label="`Enter value for ${name}`"
        :min="min"
        :max="max"
        :errors="showErrorMessage ? [' '] : null"
      />
      <template v-if="rule.operator === 'between'">
        <span class="uppercase text-muted-foreground">{{ t('global.words.and') }}</span>
        <AppFormInput
          :id="`${name}-second-value`"
          v-model="rule.value[1]"
          type="number"
          class="h-full w-full sm:flex 2xl:justify-end"
          :disabled="disabled"
          :aria-label="`Enter second value for ${name}`"
          :min="min"
          :max="max"
          :errors="showErrorMessage ? [' '] : null"
        />
      </template>
      <span v-if="suffix" class="text-muted-foreground">{{ suffix }}</span>
    </div>
    <span v-if="showErrorMessage" class="text-sm text-red-500 sm:col-span-2">
      {{ t('pages.audience.inputComparison.error') }}
    </span>
  </div>
</template>
